import {
  STORAGE_USER_KEY,
  STORAGE_RECENT_SEARCH_KEY,
  STORAGE_RECENT_ACTIVITY_EVENT_TYPES_KEY,
  STORAGE_RECENT_ACTIVITY_DATE_RANGE_KEY
} from "../constants/global"
import {
  ACTIVITY_FILTER_TYPE_VALUES,
  DEFAULT_SELECTED_ACTIVITY_TYPES
} from "../constants/activity"
import { SearchResult } from "../../types"

const MAX_RECENT_SEARCHES = 10
const VALID_ACTIVITY_TYPES = new Set<string>(ACTIVITY_FILTER_TYPE_VALUES)
const VALID_DATE_RANGE_PRESETS = new Set([
  "all",
  "today",
  "last_7",
  "last_30",
  "last_90",
  "custom"
])

export type RecentActivityDateRangePreset =
  | "all"
  | "today"
  | "last_7"
  | "last_30"
  | "last_90"
  | "custom"

export type RecentActivityDatePreference = {
  range: RecentActivityDateRangePreset
  customStartDate: Date | null
  customEndDate: Date | null
}

const DEFAULT_DATE_PREFERENCE: RecentActivityDatePreference = {
  range: "all",
  customStartDate: null,
  customEndDate: null
}

export const getBBUserFromLocalStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_USER_KEY)
    return data ? JSON.parse(data) : null
  } catch (error) {
    return null
  }
}

export const getSessionIdFromLocalStorage = () => {
  try {
    const sessionId = JSON.parse(
      localStorage.getItem(STORAGE_USER_KEY) ?? "{}"
    )?.sessid
    return sessionId
  } catch (error) {
    return null
  }
}

export const setBBUserInLocalStorage = (data: any) =>
  localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(data))

export const removeBBUserFromLocalStorage = () =>
  localStorage.removeItem(STORAGE_USER_KEY)

export const getRecentSearches = (): SearchResult[] => {
  try {
    const data = JSON.parse(
      localStorage.getItem(STORAGE_RECENT_SEARCH_KEY) ?? "[]"
    )
    return data
  } catch (error) {
    return []
  }
}

export const setRecentSearches = (data: SearchResult[]) =>
  localStorage.setItem(STORAGE_RECENT_SEARCH_KEY, JSON.stringify(data))

export const addRecentSearch = (search: SearchResult) => {
  const list = [
    search,
    ...getRecentSearches().filter(
      item => !(item.id === search.id && item.type === search.type)
    )
  ]

  // Store recent 10 results
  setRecentSearches(list.slice(Math.max(list.length - MAX_RECENT_SEARCHES, 0)))
}

const sanitizeActivityEventTypes = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null

  const sanitized: string[] = []
  for (const item of value) {
    if (typeof item !== "string" || !VALID_ACTIVITY_TYPES.has(item)) continue
    if (sanitized.includes(item)) continue
    sanitized.push(item)
  }

  // Non-array or all-invalid → treat as missing; empty array is a valid preference
  if (value.length > 0 && sanitized.length === 0) return null
  return sanitized
}

export const getRecentActivityEventTypes = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_RECENT_ACTIVITY_EVENT_TYPES_KEY)
    if (raw === null) return [...DEFAULT_SELECTED_ACTIVITY_TYPES]

    const sanitized = sanitizeActivityEventTypes(JSON.parse(raw))
    return sanitized ?? [...DEFAULT_SELECTED_ACTIVITY_TYPES]
  } catch {
    return [...DEFAULT_SELECTED_ACTIVITY_TYPES]
  }
}

export const setRecentActivityEventTypes = (types: string[]) => {
  const sanitized = sanitizeActivityEventTypes(types) ?? [
    ...DEFAULT_SELECTED_ACTIVITY_TYPES
  ]
  localStorage.setItem(
    STORAGE_RECENT_ACTIVITY_EVENT_TYPES_KEY,
    JSON.stringify(sanitized)
  )
}

const parseStoredDate = (value: unknown): Date | null => {
  if (typeof value !== "string" || value.length === 0) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

const sanitizeDatePreference = (
  value: unknown
): RecentActivityDatePreference | null => {
  if (!value || typeof value !== "object") return null

  const range = (value as { range?: unknown }).range
  if (typeof range !== "string" || !VALID_DATE_RANGE_PRESETS.has(range)) {
    return null
  }

  if (range !== "custom") {
    return {
      range: range as RecentActivityDateRangePreset,
      customStartDate: null,
      customEndDate: null
    }
  }

  const customStartDate = parseStoredDate(
    (value as { customStartDate?: unknown }).customStartDate
  )
  const customEndDate = parseStoredDate(
    (value as { customEndDate?: unknown }).customEndDate
  )

  if (!customStartDate || !customEndDate) return null
  if (customStartDate.getTime() > customEndDate.getTime()) return null

  return {
    range: "custom",
    customStartDate,
    customEndDate
  }
}

export const getRecentActivityDatePreference =
  (): RecentActivityDatePreference => {
    try {
      const raw = localStorage.getItem(STORAGE_RECENT_ACTIVITY_DATE_RANGE_KEY)
      if (raw === null) return { ...DEFAULT_DATE_PREFERENCE }

      return (
        sanitizeDatePreference(JSON.parse(raw)) ?? {
          ...DEFAULT_DATE_PREFERENCE
        }
      )
    } catch {
      return { ...DEFAULT_DATE_PREFERENCE }
    }
  }

export const setRecentActivityDatePreference = (
  preference: RecentActivityDatePreference
) => {
  const isValidCustomRange =
    preference.customStartDate &&
    preference.customEndDate &&
    preference.customStartDate.getTime() <= preference.customEndDate.getTime()

  const toStore: RecentActivityDatePreference =
    preference.range !== "custom"
      ? { range: preference.range, customStartDate: null, customEndDate: null }
      : isValidCustomRange
      ? preference
      : DEFAULT_DATE_PREFERENCE

  localStorage.setItem(
    STORAGE_RECENT_ACTIVITY_DATE_RANGE_KEY,
    JSON.stringify({
      range: toStore.range,
      customStartDate: toStore.customStartDate?.toISOString() ?? null,
      customEndDate: toStore.customEndDate?.toISOString() ?? null
    })
  )
}
